import { creatorbase } from '@round/api';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { usePoolsListInfinite, useUsersListQueries } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { PAGE_SIZE } from '../components/CreatorPoolsTable/helpers';

export function useCreatorPoolsList() {
    const { selectedClientId } = useClientSelect();

    const list = usePoolsListInfinite(
        { page_size: PAGE_SIZE, client_id: selectedClientId },
        { enabled: typeof selectedClientId === 'number' }
    );

    const users = useUsersListQueries(
        list.pages.map((page) => {
            const ids = uniq(page.results.map((pool) => pool.created_by));
            return {
                params: { id: ids.join(','), page_size: ids.length },
                config: { enabled: ids.length > 0 },
            };
        }),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((query) => query.data?.results ?? []),
                    'id'
                ),
                isLoadingForPage: (pageIndex: number) => Boolean(queries[pageIndex]?.isLoading),
            }),
        }
    );

    const rows = list.data.map((pool) => ({
        ...pool,
        createdBy: users.data.find((user) => user.id === pool.created_by) ?? null,
    }));

    const getIsUserLoading = (pool: Pick<creatorbase.Pool, 'id'>) => {
        const pageIndex = list.pages.findIndex((page) => page.results.some((result) => result.id === pool.id));
        return pageIndex === -1 ? false : users.isLoadingForPage(pageIndex);
    };

    return {
        rows,
        count: list.count,
        isLoading: list.isLoading,
        fetchNextPage: list.fetchNextPage,
        hasNextPage: list.hasNextPage,
        isFetchingNextPage: list.isFetchingNextPage,
        hasError: list.hasError,
        getIsUserLoading,
    };
}
